/**
 * 全局弹窗增强工具 — 拖拽 + 8方向缩放
 *
 * 设计原则：
 *  - 零侵入：通过全局事件委托实现，无需修改任何现有 Modal 组件代码
 *  - 全覆盖：支持 <Modal>、Modal.confirm()、ProComponents ModalForm 等所有弹窗
 *  - 轻量级：纯 DOM 操作 + CSS transform，不依赖第三方库
 *
 * 拖拽：mousedown 在 .ant-modal-header 上触发，通过 transform:translate 移动弹窗
 * 缩放：鼠标贴近 .ant-modal-content 边缘 8px 内时显示 resize 光标，拖拽边缘调整宽高
 *       支持 8 方向 — n/s/e/w/ne/nw/se/sw
 *
 * 约束：
 *  - 最小宽 320px，最小高 200px
 *  - 不超过视口（保留 40px 边距）
 */

// ─── 常量 ────────────────────────────────────────────────

/** 边缘触发缩放的像素距离 */
const RESIZE_EDGE = 8;

/** 弹窗最小宽度 */
const MIN_WIDTH = 320;

/** 弹窗最小高度 */
const MIN_HEIGHT = 200;

/** 弹窗与视口边缘的最小距离 */
const VIEWPORT_PADDING = 40;

// ─── 类型 ────────────────────────────────────────────────

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface DragState {
  modal: HTMLElement;
  startX: number;
  startY: number;
  translateX: number;
  translateY: number;
  /** 弹窗不含 transform 时的屏幕 left（即 antd 排版位置） */
  baseLeft: number;
  /** 弹窗不含 transform 时的屏幕 top */
  baseTop: number;
  modalWidth: number;
  modalHeight: number;
  /** 拖拽轴向：'x' 仅水平（弹窗带 data-drag-axis="x" 时），'both' 全方向 */
  dragAxis: 'both' | 'x';
}

interface ResizeState {
  modal: HTMLElement;
  content: HTMLElement;
  direction: ResizeDirection;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
  startTranslateX: number;
  startTranslateY: number;
}

// ─── 工具函数 ────────────────────────────────────────────

/**
 * 从 CSS transform 中提取 translate 值
 * antd Modal 默认无 transform，返回 { x: 0, y: 0 }
 */
function getTranslate(el: HTMLElement): { x: number; y: number } {
  const style = window.getComputedStyle(el);
  if (!style.transform || style.transform === 'none') {
    return { x: 0, y: 0 };
  }
  const matrix = new DOMMatrixReadOnly(style.transform);
  return { x: matrix.m41, y: matrix.m42 };
}

/**
 * 判断鼠标是否在元素边缘（用于缩放触发）
 */
function getResizeDirection(
  mouseX: number,
  mouseY: number,
  rect: DOMRect,
): ResizeDirection | null {
  const nearTop = mouseY - rect.top <= RESIZE_EDGE;
  const nearBottom = rect.bottom - mouseY <= RESIZE_EDGE;
  const nearLeft = mouseX - rect.left <= RESIZE_EDGE;
  const nearRight = rect.right - mouseX <= RESIZE_EDGE;

  if (nearTop && nearLeft) return 'nw';
  if (nearTop && nearRight) return 'ne';
  if (nearBottom && nearLeft) return 'sw';
  if (nearBottom && nearRight) return 'se';
  if (nearTop) return 'n';
  if (nearBottom) return 's';
  if (nearLeft) return 'w';
  if (nearRight) return 'e';

  return null;
}

function cursorFor(direction: ResizeDirection): string {
  const map: Record<ResizeDirection, string> = {
    n: 'ns-resize',
    s: 'ns-resize',
    e: 'ew-resize',
    w: 'ew-resize',
    ne: 'nesw-resize',
    sw: 'nesw-resize',
    nw: 'nwse-resize',
    se: 'nwse-resize',
  };
  return map[direction];
}

/**
 * 在弹窗根节点上添加/移除 CSS 类
 */
function getModalRoot(modal: HTMLElement): HTMLElement | null {
  return modal.closest('.ant-modal-root') as HTMLElement | null;
}

// ─── 核心类 ──────────────────────────────────────────────

class ModalEnhancer {
  private drag: DragState | null = null;
  private resize: ResizeState | null = null;
  private hoveredContent: HTMLElement | null = null;
  private initialized = false;

  /** 启动全局增强（推荐在 app.tsx 初始化时调用一次） */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    document.addEventListener('mousedown', this.onMouseDown, true);
    document.addEventListener('mousemove', this.onMouseMove, true);
    document.addEventListener('mouseup', this.onMouseUp, true);
  }

  /** 销毁全局增强 */
  destroy(): void {
    document.removeEventListener('mousedown', this.onMouseDown, true);
    document.removeEventListener('mousemove', this.onMouseMove, true);
    document.removeEventListener('mouseup', this.onMouseUp, true);
    this.cleanup();
    this.initialized = false;
  }

  // ── 事件处理 ────────────────────────────────────────

  private onMouseDown = (e: MouseEvent): void => {
    // 忽略右键
    if (e.button !== 0) return;
    // 已在拖拽/缩放中
    if (this.drag || this.resize) return;

    const target = e.target as HTMLElement;

    // 1) 优先检测缩放：鼠标贴近 .ant-modal-content 边缘
    const content = target.closest('.ant-modal-content') as HTMLElement | null;
    if (content) {
      const modal = content.closest('.ant-modal') as HTMLElement | null;
      if (modal) {
        const direction = getResizeDirection(e.clientX, e.clientY, content.getBoundingClientRect());
        if (direction) {
          e.preventDefault();
          this.beginResize(e, modal, content, direction);
          return;
        }
      }
    }

    // 2) 检测拖拽：鼠标在 .ant-modal-header 或 .ant-modal-confirm-body（Modal.confirm）上
    const header =
      target.closest('.ant-modal-header') as HTMLElement | null;
    const confirmBody =
      target.closest('.ant-modal-confirm-body') as HTMLElement | null;
    const dragHandle = header || confirmBody;

    if (dragHandle) {
      const modal = dragHandle.closest('.ant-modal') as HTMLElement | null;
      if (modal) {
        // 忽略按钮（关闭、确认、取消等）
        if (target.closest('button')) return;
        // 忽略 confirm 对话框中的内容文本区域（仅标题行可拖拽）
        if (confirmBody && target.closest('.ant-modal-confirm-content')) return;
        e.preventDefault();
        this.beginDrag(e, modal);
        return;
      }
    }
  };

  private onMouseMove = (e: MouseEvent): void => {
    // ── 拖拽中 ──
    if (this.drag) {
      const dx = e.clientX - this.drag.startX;
      const dy = e.clientY - this.drag.startY;

      const { baseLeft, baseTop, modalWidth, modalHeight } = this.drag;

      // 目标屏幕位置 = 排版位置 + 累积拖拽偏移 + 本次增量
      let tx = this.drag.translateX + dx;
      // 仅水平拖拽（data-drag-axis="x"）时垂直偏移保持不变
      let ty = this.drag.dragAxis === 'x'
        ? this.drag.translateY
        : this.drag.translateY + dy;

      // 视口边界约束（保证至少 40px 标题栏留在屏幕内，防止完全拖出屏幕）
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const minVisible = VIEWPORT_PADDING;

      // 弹窗最终屏幕位置
      const finalLeft = baseLeft + tx;
      const finalTop = baseTop + ty;

      // 左边界：弹窗右边缘至少离左边 minVisible
      if (finalLeft + modalWidth < minVisible) {
        tx = minVisible - modalWidth - baseLeft;
      }
      // 右边界：弹窗左边缘至少离右边 minVisible
      if (finalLeft > vw - minVisible) {
        tx = vw - minVisible - baseLeft;
      }
      // 上边界：弹窗下边缘至少离上边 minVisible
      if (finalTop + modalHeight < minVisible) {
        ty = minVisible - modalHeight - baseTop;
      }
      // 下边界：弹窗上边缘至少离下边 minVisible
      if (finalTop > vh - minVisible) {
        ty = vh - minVisible - baseTop;
      }

      this.drag.modal.style.transform = `translate(${tx}px, ${ty}px)`;
      return;
    }

    // ── 缩放中 ──
    if (this.resize) {
      this.updateResize(e);
      return;
    }

    // ── 光标悬停检测 ──
    this.updateCursor(e);
  };

  private onMouseUp = (): void => {
    if (this.drag) {
      const root = getModalRoot(this.drag.modal);
      root?.classList.remove('modal-is-dragging');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.drag = null;
    }

    if (this.resize) {
      // 标记弹窗已被缩放，使其持久保持 flex 布局以正确传递高度
      this.resize.modal.setAttribute('data-modal-resized', 'true');

      const root = getModalRoot(this.resize.modal);
      root?.classList.remove('modal-is-resizing');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.resize = null;
    }
  };

  // ── 拖拽逻辑 ────────────────────────────────────────

  private beginDrag(e: MouseEvent, modal: HTMLElement): void {
    const translate = getTranslate(modal);
    const rect = modal.getBoundingClientRect();

    // 弹窗的「排版位置」= 当前屏幕位置 − 已累积的拖拽偏移
    const baseLeft = rect.left - translate.x;
    const baseTop = rect.top - translate.y;

    // 弹窗标记 data-drag-axis="x" 时仅允许水平拖拽
    const dragAxis = modal.getAttribute('data-drag-axis') === 'x' ? 'x' : 'both';

    this.drag = {
      modal,
      startX: e.clientX,
      startY: e.clientY,
      translateX: translate.x,
      translateY: translate.y,
      baseLeft,
      baseTop,
      modalWidth: rect.width,
      modalHeight: rect.height,
      dragAxis,
    };

    const root = getModalRoot(modal);
    root?.classList.add('modal-is-dragging');
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'move';
  }

  // ── 缩放逻辑 ────────────────────────────────────────

  private beginResize(
    e: MouseEvent,
    modal: HTMLElement,
    content: HTMLElement,
    direction: ResizeDirection,
  ): void {
    const modalRect = modal.getBoundingClientRect();
    const translate = getTranslate(modal);

    this.resize = {
      modal,
      content,
      direction,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: modalRect.width,
      startHeight: modalRect.height,
      startTranslateX: translate.x,
      startTranslateY: translate.y,
    };

    const root = getModalRoot(modal);
    root?.classList.add('modal-is-resizing');
    document.body.style.userSelect = 'none';
    document.body.style.cursor = cursorFor(direction);
  }

  private updateResize(e: MouseEvent): void {
    if (!this.resize) return;

    const dx = e.clientX - this.resize.startX;
    const dy = e.clientY - this.resize.startY;
    const { direction, startWidth, startHeight, startTranslateX, startTranslateY } =
      this.resize;
    const modal = this.resize.modal;

    let newWidth = startWidth;
    let newHeight = startHeight;
    let tx = startTranslateX;
    let ty = startTranslateY;

    // ── 宽度 ──
    if (direction.includes('e')) {
      // 右边缘：向右拉伸
      newWidth = clamp(startWidth + dx, MIN_WIDTH, window.innerWidth - VIEWPORT_PADDING);
    } else if (direction.includes('w')) {
      // 左边缘：向左拉伸，同时向右补偿位移
      const raw = startWidth - dx;
      newWidth = clamp(raw, MIN_WIDTH, window.innerWidth - VIEWPORT_PADDING);
      tx = startTranslateX + (startWidth - newWidth);
    }

    // ── 高度 ──
    if (direction.includes('s')) {
      // 下边缘：向下拉伸
      newHeight = clamp(startHeight + dy, MIN_HEIGHT, window.innerHeight - VIEWPORT_PADDING);
    } else if (direction.includes('n')) {
      // 上边缘：向上拉伸，同时向下补偿位移
      const raw = startHeight - dy;
      newHeight = clamp(raw, MIN_HEIGHT, window.innerHeight - VIEWPORT_PADDING);
      ty = startTranslateY + (startHeight - newHeight);
    }

    // ⚠️ 关键：高度设在 .ant-modal-content 上（而非 .ant-modal）
    // 因为 antd 块级子元素不沿高度方向拉伸父级，在 .ant-modal 上设 height
    // 无法驱动 .ant-modal-content 实际变高；视觉背景/阴影在 content 上。
    modal.style.width = `${newWidth}px`;
    this.resize.content.style.height = `${newHeight}px`;
    modal.style.transform = `translate(${tx}px, ${ty}px)`;
  }

  // ── 光标管理 ────────────────────────────────────────

  private updateCursor(e: MouseEvent): void {
    const content = (e.target as HTMLElement).closest('.ant-modal-content') as HTMLElement | null;

    // 鼠标离开之前悬停的 modal content
    if (this.hoveredContent && this.hoveredContent !== content) {
      this.hoveredContent.style.cursor = '';
      this.hoveredContent = null;
    }

    if (!content) return;

    const direction = getResizeDirection(e.clientX, e.clientY, content.getBoundingClientRect());
    // 如果鼠标在 header 区域但不贴近边缘，显示 move 光标
    const header = (e.target as HTMLElement).closest('.ant-modal-header');

    if (direction) {
      content.style.cursor = cursorFor(direction);
      this.hoveredContent = content;
    } else {
      content.style.cursor = '';
      this.hoveredContent = null;
    }

    // header 区域默认 move 光标（通过 CSS 全局设置，这里仅作 fallback）
    if (header && !direction) {
      // CSS 已处理
    }
  }

  // ── 清理 ────────────────────────────────────────────

  private cleanup(): void {
    if (this.drag) {
      const root = getModalRoot(this.drag.modal);
      root?.classList.remove('modal-is-dragging');
      this.drag = null;
    }
    if (this.resize) {
      const root = getModalRoot(this.resize.modal);
      root?.classList.remove('modal-is-resizing');
      this.resize = null;
    }
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }
}

// ─── 辅助函数 ────────────────────────────────────────────

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// ─── 导出单例 ────────────────────────────────────────────

export const modalEnhancer = new ModalEnhancer();
